import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { generateAdVariations, generateAdImage, validateAdContent, generateAdCopy, fetchPreloadedImages } from '../../utils/openaiUtils';
import FacebookAdPreview from './FacebookAdPreview';
import ImageSelector from './ImageSelector';
import ImageEditDialog from './ImageEditDialog';

const AdCreator = ({ campaignData, onAdsChange }) => {
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adVariations, setAdVariations] = useState([]);
  const [selectedVariation, setSelectedVariation] = useState(0);
  const [generatingVariation, setGeneratingVariation] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  
  // Image selector state
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false);
  
  // Image edit state
  const [imageEditOpen, setImageEditOpen] = useState(false);

  // Generate initial variations on mount
  useEffect(() => {
    generateInitialVariations();
  }, []);

  // Generate initial ad variations
  const generateInitialVariations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if we have a selected page with a slug
      if (!campaignData.selectedPage || !campaignData.selectedPage.slug) {
        console.warn('No selected page or slug available');
        setError('No page selected. Please select a page first.');
        setLoading(false);
        return;
      }
      
      // Attempt to fetch preloaded images first
      console.log(`Checking for preloaded images for slug: ${campaignData.selectedPage.slug}`);
      const preloadedImages = await fetchPreloadedImages(campaignData.selectedPage.slug, true);
      console.log(`Found ${preloadedImages.length} preloaded images`);
      
      // Create formattedPage object for consistent usage
      const formattedPage = {
        title: campaignData.selectedPage.title,
        slug: campaignData.selectedPage.slug
      };
      
      // If we have preloaded images, use them directly
      if (preloadedImages.length > 0) {
        console.log('Using preloaded images for ad variations');
        
        // Generate copy only (no images) for each variation
        const variations = [];
        const variationCount = Math.min(preloadedImages.length, 3);
        
        for (let i = 0; i < variationCount; i++) {
          // Generate just the ad copy
          const copy = await generateAdCopy({
            page: formattedPage,
            objective: campaignData.campaign.objective,
            variationIndex: i + 1,
          });
          
          // Use preloaded image
          const image = preloadedImages[i];
          
          // Create variation
          variations.push({
            copy,
            image,
            variationId: `var_${i + 1}`,
          });
        }
        
        // Validate each variation
        const validatedVariations = variations.map(variation => ({
          ...variation,
          validation: validateAdContent(variation)
        }));

        setAdVariations(validatedVariations);
        onAdsChange(validatedVariations);
      } else {
        console.log('No preloaded images found, will need to generate new ones');
        
        // Show a message to the user
        setError('No preloaded images found. Press the image button to generate new images.');
        
        // Create empty variations with just copy
        const variations = [];
        const variationCount = 3;
        
        for (let i = 0; i < variationCount; i++) {
          // Generate just the ad copy
          const copy = await generateAdCopy({
            page: formattedPage,
            objective: campaignData.campaign.objective,
            variationIndex: i + 1,
          });
          
          // Use a placeholder image until user generates a real one
          const image = {
            url: '/placeholder-image.svg'  // Placeholder SVG
          };
          
          // Create variation
          variations.push({
            copy,
            image,
            variationId: `var_${i + 1}`,
            needsImage: true  // Flag to indicate this variation needs an image
          });
        }
        
        // Validate each variation
        const validatedVariations = variations.map(variation => ({
          ...variation,
          validation: validateAdContent(variation)
        }));

        setAdVariations(validatedVariations);
        onAdsChange(validatedVariations);
      }
    } catch (err) {
      setError('Failed to generate ad variations: ' + err.message);
      console.error('Error in generateInitialVariations:', err);
    } finally {
      setLoading(false);
    }
  };

  // Generate a new variation
  const handleGenerateNewVariation = async () => {
    setGeneratingVariation(true);
    try {
      const newVariations = await generateAdVariations(
        campaignData.selectedPage,
        campaignData.campaign.objective,
        1
      );
      
      const validatedVariation = {
        ...newVariations[0],
        validation: validateAdContent(newVariations[0])
      };

      setAdVariations(prev => [...prev, validatedVariation]);
      onAdsChange([...adVariations, validatedVariation]);
    } catch (err) {
      setError('Failed to generate new variation: ' + err.message);
    } finally {
      setGeneratingVariation(false);
    }
  };

  // Open image selector modal
  const handleOpenImageSelector = () => {
    if (!campaignData.selectedPage || !campaignData.selectedPage.slug) {
      setError('No page selected. Please select a page first.');
      return;
    }
    setImageSelectorOpen(true);
  };

  // Handle selected image from selector
  const handleSelectImage = (image) => {
    // Update the current variation with the selected image
    const updatedVariations = [...adVariations];
    updatedVariations[selectedVariation] = {
      ...updatedVariations[selectedVariation],
      image: image,
      needsImage: false // Remove the flag indicating this variation needs an image
    };

    setAdVariations(updatedVariations);
    onAdsChange(updatedVariations);
    
    // Close the selector modal
    setImageSelectorOpen(false);
    
    // Clear any error message that might have been set
    setError(null);
  };

  // Generate a new image for the current variation
  const handleGenerateNewImage = async () => {
    if (!campaignData.selectedPage) return;
    
    setGeneratingImage(true);
    setError(null);
    
    try {
      // Generate new image for the current variation
      const newImage = await generateAdImage({
        page: {
          title: campaignData.selectedPage.title,
          slug: campaignData.selectedPage.slug || 'default'
        },
        style: 'professional'
      });

      // Update the current variation with the new image
      const updatedVariations = [...adVariations];
      updatedVariations[selectedVariation] = {
        ...updatedVariations[selectedVariation],
        image: newImage,
        needsImage: false // Remove the flag indicating this variation needs an image
      };

      setAdVariations(updatedVariations);
      onAdsChange(updatedVariations);
      
      // Clear any error message that might have been set
      setError(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      
      // Check for specific OpenAI safety system rejection
      if (errorMessage.includes('safety system') || errorMessage.includes('content policy')) {
        setError('OpenAI rejected the image generation request due to content policy. Try different wording for your page title.');
      } else {
        setError('Failed to generate new image: ' + errorMessage);
      }
      
      console.error('Error generating image:', err);
    } finally {
      setGeneratingImage(false);
    }
  };

  // Handle variation selection
  const handleVariationChange = (event, newValue) => {
    setSelectedVariation(newValue);
  };

  // Handle variation deletion
  const handleDeleteVariation = (index) => {
    const updatedVariations = adVariations.filter((_, i) => i !== index);
    setAdVariations(updatedVariations);
    onAdsChange(updatedVariations);
    if (selectedVariation >= updatedVariations.length) {
      setSelectedVariation(Math.max(0, updatedVariations.length - 1));
    }
  };

  // Handle regeneration of a specific variation
  const handleRegenerateVariation = async (index) => {
    setGeneratingVariation(true);
    try {
      const newVariations = await generateAdVariations(
        campaignData.selectedPage,
        campaignData.campaign.objective,
        1
      );
      
      const validatedVariation = {
        ...newVariations[0],
        validation: validateAdContent(newVariations[0])
      };

      const updatedVariations = [...adVariations];
      updatedVariations[index] = validatedVariation;
      
      setAdVariations(updatedVariations);
      onAdsChange(updatedVariations);
    } catch (err) {
      setError('Failed to regenerate variation: ' + err.message);
    } finally {
      setGeneratingVariation(false);
    }
  };

  // Handle ad copy update
  const handleAdUpdate = (index, updatedAd) => {
    const updatedVariations = [...adVariations];
    updatedVariations[index] = {
      ...updatedAd,
      validation: validateAdContent(updatedAd)
    };
    setAdVariations(updatedVariations);
    onAdsChange(updatedVariations);
  };

  // Handle image edit
  const handleOpenImageEdit = () => {
    setImageEditOpen(true);
  };

  const handleImageEdited = (editedImageUrl) => {
    const currentVariation = adVariations[selectedVariation];
    if (currentVariation) {
      const updatedAd = {
        ...currentVariation,
        image: {
          ...currentVariation.image,
          url: editedImageUrl,
          edited: true
        }
      };
      handleAdUpdate(selectedVariation, updatedAd);
    }
    setImageEditOpen(false);
  };

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Variations Tabs */}
          <Grid item xs={12}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={selectedVariation}
                onChange={handleVariationChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                {adVariations.map((variation, index) => (
                  <Tab
                    key={variation.variationId}
                    label={`Variation ${index + 1}`}
                    icon={
                      variation.validation.isValid ? null : (
                        <Tooltip title="Has validation issues">
                          <Alert severity="warning" sx={{ p: 0, minWidth: 'auto' }}>
                            !
                          </Alert>
                        </Tooltip>
                      )
                    }
                  />
                ))}
                <Tab
                  icon={<AutoFixHighIcon />}
                  label="New Variation"
                  onClick={handleGenerateNewVariation}
                  disabled={generatingVariation}
                />
              </Tabs>
            </Box>
          </Grid>

          {/* Selected Variation Content */}
          {adVariations[selectedVariation] && (
            <>
              {/* Ad Preview */}
              <Grid item xs={12} md={8}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Tooltip title="Select from image library">
                    <IconButton
                      onClick={handleOpenImageSelector}
                      color="primary"
                    >
                      <PhotoLibraryIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Generate a new image for this variation">
                    <IconButton
                      onClick={handleGenerateNewImage}
                      disabled={generatingImage}
                      color={adVariations[selectedVariation]?.needsImage ? "warning" : "primary"}
                    >
                      {generatingImage ? <CircularProgress size={24} /> : <ImageIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit current image with AI">
                    <IconButton
                      onClick={handleOpenImageEdit}
                      disabled={!adVariations[selectedVariation]?.image?.url || 
                               adVariations[selectedVariation]?.image?.url.includes('placeholder-image')}
                      color="primary"
                    >
                      <AutoFixHighIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Regenerate this variation">
                    <IconButton
                      onClick={() => handleRegenerateVariation(selectedVariation)}
                      disabled={generatingVariation}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete this variation">
                    <IconButton
                      onClick={() => handleDeleteVariation(selectedVariation)}
                      disabled={adVariations.length <= 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Facebook Ad Preview */}
                <FacebookAdPreview 
                  adData={adVariations[selectedVariation]} 
                  onUpdate={(updatedAd) => handleAdUpdate(selectedVariation, updatedAd)}
                />
              </Grid>

              {/* Validation Panel */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Validation Results
                  </Typography>
                  
                  {adVariations[selectedVariation].validation.isValid ? (
                    <Alert severity="success">
                      This ad variation meets all requirements
                    </Alert>
                  ) : (
                    <Alert severity="warning">
                      <Typography variant="subtitle2" gutterBottom>
                        Issues found:
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                        {adVariations[selectedVariation].validation.issues.map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                  
                  {/* Show recommendations if any exist */}
                  {adVariations[selectedVariation].validation.recommendations && 
                   adVariations[selectedVariation].validation.recommendations.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info">
                        <Typography variant="subtitle2" gutterBottom>
                          Recommendations:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                          {adVariations[selectedVariation].validation.recommendations.map((rec, i) => (
                            <li key={i}>{rec}</li>
                          ))}
                        </ul>
                      </Alert>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </>
          )}
        </Grid>
      )}

      {/* Image Selector Modal */}
      <ImageSelector
        open={imageSelectorOpen}
        onClose={() => setImageSelectorOpen(false)}
        slug={campaignData.selectedPage?.slug}
        onSelectImage={handleSelectImage}
      />

      {/* Image Edit Dialog */}
      {adVariations[selectedVariation]?.image?.url && (
        <ImageEditDialog
          open={imageEditOpen}
          onClose={() => setImageEditOpen(false)}
          imageUrl={adVariations[selectedVariation].image.url}
          onImageEdited={handleImageEdited}
        />
      )}
    </Box>
  );
};

export default AdCreator; 